import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface CatalogoPublico {
  code: number;
  description: string;
}

// Metadato que devuelve /public/catalogs para cada catálogo disponible.
export interface CatalogoMeta {
  key: string;
  scope: string;
  modules: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CuestionarioV2Service {

  private apiURL = environment.api_cuestionariosA_rest;
  private apiBIURL = environment.api_general;
  private headers = new HttpHeaders().append('Content-Type', 'application/json')
    .append('Authorization', 'Bearer ');

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
  ) { }

  public getCaptcha() {
    const apiURL = this.apiURL + 'anonymous-responses/captcha';
    return this.http.get(`${apiURL}`);
  }

  public verifyCaptcha(captchaToken: string) {
    const apiURL = this.apiURL + 'anonymous-responses/verify-captcha';
    const body = JSON.stringify({ captchaToken });
    return this.http.post(`${apiURL}`, body, { headers: this.headers });
  }

  public postAnnonymousResponses(payload) {
    const apiURL = this.apiURL + 'anonymous-responses';
    return this.http.post(`${apiURL}`, payload, { headers: this.headers });
  }

  // Trae un catálogo público por su key para una institución (sin login).
  public getPublicCatalog(catalogKey: string, institutionHash: string): Observable<CatalogoPublico[]> {
    const apiURL = `${this.apiBIURL}public/catalogs/${catalogKey}?ih=${institutionHash}`;
    return this.http.get<CatalogoPublico[]>(apiURL);
  }

  // Descubre dinámicamente qué catálogos aplican al cuestionario anónimo:
  // consulta el listado y se queda solo con los que incluyen el módulo 'survey'.
  // Así, si el back agrega un catálogo nuevo de survey, aparece automáticamente
  // sin tocar el front.
  public getSurveyCatalogKeys(): Observable<string[]> {
    const apiURL = `${this.apiBIURL}public/catalogs`;
    return this.http.get<CatalogoMeta[]>(apiURL).pipe(
      map(catalogs => (catalogs || [])
        .filter(c => Array.isArray(c.modules) && c.modules.includes('survey'))
        .map(c => c.key))
    );
  }

  // Trae en paralelo todos los catálogos del módulo survey y regresa un objeto
  // { [catalogKey]: CatalogoPublico[] }. Descubre los keys con
  // getSurveyCatalogKeys y luego pide cada catálogo; si uno falla, regresa []
  // para ese key sin tumbar a los demás.
  public getSurveyCatalogs(institutionHash: string): Observable<Record<string, CatalogoPublico[]>> {
    return this.getSurveyCatalogKeys().pipe(
      switchMap(keys => {
        if (!keys.length) {
          return of({} as Record<string, CatalogoPublico[]>);
        }
        const requests: Record<string, Observable<CatalogoPublico[]>> = {};
        for (const key of keys) {
          requests[key] = this.getPublicCatalog(key, institutionHash).pipe(
            catchError(() => of([] as CatalogoPublico[]))
          );
        }
        return forkJoin(requests);
      })
    );
  }

}
